import CorporateCourseMenuRedirectMiddleware from '@/middleware/CorporateCourseMenuRedirectMiddleware.js';
import GateKeeperMiddleware from '@/middleware/GateKeeperMiddleware.js';
import { redirectToLegacy, redirectToRedesign } from '@/router/courses/gradebook/gradebookRedesignGuards.js';

/**
 * Gradebook routes, legacy and redesign side by side behind `GRADEBOOK_REDESIGN`.
 *
 * Three things about the wiring are load-bearing:
 *
 *  1. **Every record declares `meta.middleware` AND `meta.ability`.** `callRouteMetaMiddleware`
 *     (router/index.js) filters `to.matched` down to records DECLARING `meta.middleware` and
 *     only then runs `GateKeeperMiddleware` against `meta.ability`; a record without it is
 *     skipped and `next()` proceeds. The legacy grader is protected only because its
 *     intermediate parent carries them — its named child has none — so copying the child's
 *     shape onto a new route would ship it ungated.
 *  2. **`props: true` is required.** Without it `courseId` arrives `undefined` under the
 *     parent's `v-bind="route"` slot.
 *  3. **`beforeEnter` becomes an ARRAY on the routes that gained a guard.** That breaks the
 *     strict `.toBe(CorporateCourseMenuRedirectMiddleware)` identity checks in
 *     `CorporateCourseMenuRedirectMiddleware.spec.js`, which is updated to normalise with
 *     `[].concat(...)`. The corporate redirect stays FIRST: a corporate user must be bounced
 *     off the gradebook entirely before the flag gets a say in which gradebook they see.
 *
 * The grader IS redirected as of wave 4: `gradebook-grader` now exists, so
 * `redirectToRedesign` on the legacy grader resolves. The guard sits on the INTERMEDIATE
 * record rather than the named child, because that is the record a navigation to
 * `gradebook-activity-grader` actually enters through.
 *
 * `gradebook-audit-log` IS redirected as of wave 5 prep, now that the grid carries an audit
 * tab: the guard sends it to `gradebook-grid` with `?tab=audit` so the surface survives the
 * hop. Until that tab existed, redirecting would have swallowed it.
 */
export default [
    {
        path: '/courses/:courseId/gradebook/:gradebookId?',
        component: () => import('@/pages/courses/gradebook/Gradebook.vue'),
        props: true,

        children: [
            {
                name: 'gradebook-view',
                path: '',
                component: () => import('@/components/gradebook/GradebookView.vue'),
                beforeEnter: [CorporateCourseMenuRedirectMiddleware, redirectToRedesign],
                meta: {
                    title: 'Gradebook',
                    dataTablePage: true,
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view', subject: 'gradebook' },
                },
                props: true,
            },
            {
                // Static `grid` must outrank the parent's optional `:gradebookId?`, or
                // `/courses/1/gradebook/grid` resolves to the LEGACY grid with
                // `gradebookId === 'grid'`. `Gradebook.routes.test.js` resolves that exact
                // URL against a real router rather than taking vue-router's ranking on
                // trust.
                name: 'gradebook-grid',
                path: 'grid',
                component: () => import('@/pages/courses/gradebook/GradebookGridPage.vue'),
                beforeEnter: [CorporateCourseMenuRedirectMiddleware, redirectToLegacy],
                meta: {
                    title: 'Gradebook',
                    dataTablePage: true,
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view', subject: 'gradebook' },
                },
                props: true,
            },
            {
                /**
                 * The audit log is a TAB on the redesigned grid, not a route of its own —
                 * so the redirect carries `?tab=audit` rather than pointing at a record
                 * that does not exist. It was left unguarded until wave 3b built that tab,
                 * because sending it to the bare grid would have swallowed the surface
                 * instead of redesigning it.
                 */
                name: 'gradebook-audit-log',
                path: 'audit-log',
                component: () => import('@/components/gradebook/GradebookView.vue'),
                beforeEnter: [CorporateCourseMenuRedirectMiddleware, redirectToRedesign],
                meta: {
                    title: 'Gradebook Audit Log',
                    dataTablePage: true,
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view', subject: 'gradebook' },
                },
                props: true,
            },
            {
                /**
                 * The redesigned grader.
                 *
                 * `grader/` is a STATIC first segment for the same reason `grid` is: without
                 * it the parent's optional `:gradebookId?` would swallow it.
                 *
                 * The param NAMES and their required/optional-ness match the legacy record
                 * exactly, including `:submissionId`. `redirectToRedesign` and
                 * `redirectToLegacy` forward `to.params` verbatim, so a mismatch would make
                 * one direction unbuildable — a learner with no submission uses the `-1`
                 * sentinel in the URL, as legacy does.
                 *
                 * `meta.middleware` and `meta.ability` sit HERE, on the record that renders.
                 * The legacy grader is gated only because its intermediate PARENT carries
                 * them and its named child does not; copying the child's shape would ship
                 * this ungated.
                 */
                name: 'gradebook-grader',
                path: 'grader/activities/:activityId/user/:userId/:submissionId',
                component: () => import('@/pages/courses/gradebook/GradebookGraderPage.vue'),
                beforeEnter: [CorporateCourseMenuRedirectMiddleware, redirectToLegacy],
                meta: {
                    title: 'Gradebook',
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view', subject: 'gradebook' },
                },
                props: true,
            },
            {
                path: 'activities/:activityId/user/:userId/activity-grader/:submissionId',
                beforeEnter: [redirectToRedesign],
                component: () => import('@/pages/courses/activity-grader/ActivityGrader.vue'),
                meta: {
                    title: 'Gradebook',
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view', subject: 'gradebook' },
                },
                props: true,
                children: [
                    {
                        name: 'gradebook-activity-grader',
                        path: '',
                        component: () => import('@/components/activity-grader/ActivityGraderView.vue'),
                    },
                ],
            },
        ],
    },
];
